/** Account deletion flow: reauth, confirm email, delete data + Auth user. */
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reauthenticateUser, getAuthErrorKey } from '@/services/authService';
import {
  executeAccountDeletion,
  ACCOUNT_DELETE_AUTH_USER_FAILED,
} from '@/services/accountDeleteService';
import { resolveAccountSignInMethod } from '@/features/settings/utils/accountSignInMethod';

/** Query flag shown on the login page after a successful self-delete. */
export const ACCOUNT_DELETED_LOGIN_QUERY = 'accountDeleted';

/**
 * @param {{ user: import('firebase/auth').User | null | undefined }} params
 */
export function useAccountDelete({ user }) {
  const navigate = useNavigate();
  const signInMethod = resolveAccountSignInMethod(user);
  const accountEmail = user?.email ?? '';

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthPasswordOpen, setReauthPasswordOpen] = useState(false);
  const [deleteErrorKey, setDeleteErrorKey] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const deleteInFlight = useRef(false);

  const closeDialogs = useCallback(() => {
    setConfirmOpen(false);
    setReauthPasswordOpen(false);
    setReauthPassword('');
    setDeleteErrorKey(null);
  }, []);

  const runDeletion = useCallback(async () => {
    if (!user?.uid || deleteInFlight.current) {
      return;
    }
    deleteInFlight.current = true;
    setDeleteInProgress(true);
    setDeleteErrorKey(null);
    try {
      await executeAccountDeletion(user.uid);
      navigate(`/?${ACCOUNT_DELETED_LOGIN_QUERY}=1`, { replace: true });
    } catch (error) {
      if (error?.code === ACCOUNT_DELETE_AUTH_USER_FAILED) {
        setDeleteErrorKey('settings.account.delete.errorAuthUserRemains');
      } else {
        setDeleteErrorKey('settings.account.delete.errorData');
      }
      setDeleteInProgress(false);
      deleteInFlight.current = false;
    }
  }, [navigate, user?.uid]);

  const afterReauthSuccess = useCallback(() => {
    setReauthPasswordOpen(false);
    setReauthPassword('');
    setConfirmOpen(true);
  }, []);

  const runReauth = useCallback(
    async (password) => {
      if (!user || deleteInFlight.current) {
        return false;
      }
      setDeleteErrorKey(null);
      try {
        const options =
          signInMethod === 'password' ? { password } : undefined;
        await reauthenticateUser(signInMethod, options);
        afterReauthSuccess();
        return true;
      } catch (error) {
        setDeleteErrorKey(getAuthErrorKey(error));
        return false;
      }
    },
    [afterReauthSuccess, signInMethod, user],
  );

  const beginAccountDelete = useCallback(() => {
    if (!user?.uid || deleteInFlight.current) {
      return;
    }
    setDeleteErrorKey(null);
    if (signInMethod === 'password') {
      setReauthPasswordOpen(true);
      return;
    }
    void runReauth();
  }, [runReauth, signInMethod, user?.uid]);

  const submitReauthPassword = useCallback(() => {
    void runReauth(reauthPassword);
  }, [reauthPassword, runReauth]);

  const confirmAccountDelete = useCallback(
    (typedEmail) => {
      if (typedEmail.trim() !== accountEmail.trim()) {
        setDeleteErrorKey('settings.account.delete.errorEmailMismatch');
        return;
      }
      void runDeletion();
    },
    [accountEmail, runDeletion],
  );

  const cancelAccountDelete = useCallback(() => {
    if (deleteInProgress) {
      return;
    }
    closeDialogs();
  }, [closeDialogs, deleteInProgress]);

  return {
    signInMethod,
    confirmOpen,
    reauthPasswordOpen,
    reauthPassword,
    setReauthPassword,
    deleteErrorKey,
    deleteInProgress,
    beginAccountDelete,
    submitReauthPassword,
    confirmAccountDelete,
    cancelAccountDelete,
  };
}
